import React, { useEffect, useState, useCallback, useRef } from 'react'; 
import { useNavigate, useLocation } from 'react-router-dom';
import api, { subscriptionAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { refreshSubscription } = useSubscription();
  const { refreshUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  // Add refs to track state
  const hasProcessedRef = useRef(false);
  const toastIdRef = useRef(null);
  const redirectTimeoutRef = useRef(null);

  const validateParameters = useCallback(() => {
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get('session_id');
    const restaurantId = queryParams.get('restaurant_id');

    if (!sessionId) {
      throw new Error(t('subscription.errors.missingSessionId', 'Missing session ID'));
    }

    if (!restaurantId) {
      throw new Error(t('subscription.errors.missingRestaurantId', 'Missing restaurant ID'));
    }

    return { sessionId, restaurantId };
  }, [location.search, t]);

  // Function to refresh all subscription and user data
  const refreshAllData = useCallback(async () => {
    try {
      console.log('🔄 Refreshing subscription and user data...');

      // 1. Sync subscription with Stripe
      await subscriptionAPI.syncSubscription();

      // 2. Refresh subscription context
      await refreshSubscription();

      // 3. Refresh user data in AuthContext
      if (refreshUser) {
        await refreshUser();
      } else {
        // Fallback: manually update localStorage user data
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (subscriptionDetails?.plan) {
            user.subscription_plan = subscriptionDetails.plan;
            localStorage.setItem('user', JSON.stringify(user));
          }
        }
      }

      console.log('✅ All data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing data:', error);
    }
  }, [refreshSubscription, refreshUser, subscriptionDetails?.plan]); // Only depend on plan

  const handleSuccess = useCallback(async () => {
    // Prevent multiple calls
    if (hasProcessedRef.current) {
      console.log('⚠️ Already processed subscription, skipping...');
      return;
    }

    hasProcessedRef.current = true;
    setLoading(true);

    try {
      const { sessionId, restaurantId } = validateParameters();

      console.log('🎯 Processing subscription activation...', { sessionId, restaurantId });

      const response = await api.post('/subscription.php', {
        action: 'checkout_success',
        session_id: sessionId,
        restaurant_id: restaurantId
      });

      console.log('📦 Subscription API response:', response);

      if (response.success) {
        setMessage(t('subscription.success.message', 'Subscription activated successfully!'));
        setIsSuccess(true);
        setSubscriptionDetails(response.data);

        // Refresh all data immediately
        await refreshAllData();

        // Use a single toast - remove any existing one first
        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current);
        }

        toastIdRef.current = toast.success('Subscription upgraded successfully! 🎉', {
          duration: 4000,
          position: 'top-center',
          id: 'subscription-success' // Fixed ID to prevent duplicates
        });

        // Clear any existing timeout
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
        }

        // Short delay before redirect to ensure data is refreshed
        redirectTimeoutRef.current = setTimeout(() => {
          navigate('/dashboard', {
            state: {
              subscriptionActivated: true,
              plan: response.data?.plan || 'pro',
              timestamp: Date.now()
            },
            replace: true
          });
        }, 2000);
      } else {
        throw new Error(response.error || t('subscription.errors.activationFailed', 'Failed to activate subscription'));
      }
    } catch (error) {
      console.error('❌ Subscription activation error:', error);

      const errorMessage = error.response?.data?.error ||
        error.message ||
        t('subscription.errors.unknown', 'An unknown error occurred');

      setMessage(errorMessage);
      setIsSuccess(false);

      // Use a single error toast
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
      toastIdRef.current = toast.error('Failed to activate subscription. Please try again.', {
        id: 'subscription-error'
      });
    } finally {
      setLoading(false);
    }
  }, [validateParameters, navigate, t, refreshAllData]);

  useEffect(() => {
    // Skip if already processed or no session_id
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get('session_id');

    if (!sessionId || hasProcessedRef.current) {
      return;
    }

    handleSuccess();

    // Cleanup function
    return () => {
      // Clear timeout on unmount
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }

      // Dismiss toast on unmount if still showing
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
    };
  }, [handleSuccess, location.search]); // Added location.search dependency

  // Add a manual refresh button for debugging
  const handleManualRefresh = async () => {
    // Reset processing flag
    hasProcessedRef.current = false;

    // Clear any existing toast
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
    }

    // Clear any existing timeout
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }

    setLoading(true);
    try {
      await refreshAllData();
      toast.success('Data refreshed! Please check if features are now available.', {
        id: 'manual-refresh'
      });
      // Retry the subscription success
      await handleSuccess();
    } catch (error) {
      toast.error('Refresh failed: ' + error.message, {
        id: 'refresh-error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <div className="text-center space-y-6">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-blue-600 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 bg-blue-600 rounded-full animate-pulse"></div>
            </div>
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-800">
              {t('subscription.processings.title', 'Processing Your Subscription')}
            </h2>
            <p className="text-gray-600 max-w-sm">
              {t('subscription.processings.description', 'Activating your subscription and refreshing all data...')}
            </p>
          </div>
          <div className="pt-4">
            <div className="h-1 w-48 bg-gray-200 rounded-full overflow-hidden mx-auto">
              <div className="h-full w-full bg-blue-600 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-12">
      <div className="bg-white p-8 md:p-10 rounded-2xl shadow-xl text-center max-w-md w-full border border-gray-100">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 transition-all duration-300 ${isSuccess
          ? 'bg-gradient-to-br from-green-50 to-emerald-100 shadow-lg shadow-green-100/50'
          : 'bg-gradient-to-br from-red-50 to-pink-100 shadow-lg shadow-red-100/50'
          }`}>
          {isSuccess ? (
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
          {isSuccess
            ? t('subscription.success.title', 'Success!')
            : t('subscription.errors.title', 'Oops!')}
        </h1>

        <p className={`text-lg mb-4 ${isSuccess ? 'text-gray-700' : 'text-gray-600'}`}>
          {message}
        </p>

        {isSuccess && subscriptionDetails && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Plan:</span> {subscriptionDetails.plan?.toUpperCase()}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Status:</span> {subscriptionDetails.status}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {t('subscription.success.dataRefreshed', 'All subscription data has been refreshed. You now have access to Pro features.')}
            </p>
          </div>
        )}

        <div className="space-y-4">
          <button
            onClick={() => navigate('/dashboard', {
              state: {
                subscriptionActivated: isSuccess,
                plan: subscriptionDetails?.plan,
                forceRefresh: true
              },
              replace: true
            })}
            className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-[1.02] ${isSuccess
              ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg shadow-blue-500/25'
              : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white shadow-lg shadow-gray-500/25'
              }`}
          >
            {t('common.goToDashboard', 'Go to Dashboard')}
          </button>

          {!isSuccess && (
            <button
              onClick={handleManualRefresh}
              className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-amber-700 transition-all duration-300"
            >
              {t('subscription.buttons.manualRefresh', 'Try Manual Refresh')}
            </button>
          )}

          {isSuccess && (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500 animate-pulse">
                {t('subscription.success.redirecting', 'Redirecting to dashboard...')}
              </p>
            </div>
          )}
        </div>
      </div>

      {!isSuccess && (
        <div className="mt-8 text-center max-w-md">
          <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h3 className="font-medium text-amber-800 mb-2">
              {t('subscription.troubleshooting.title', 'Troubleshooting')}
            </h3>
            <ul className="text-xs text-amber-700 text-left space-y-1">
              <li>• {t('subscription.troubleshooting.steps.manualRefresh', 'Try the "Manual Refresh" button above')}</li>
              <li>• {t('subscription.troubleshooting.steps.clearCache', 'Clear browser cache and try again')}</li>
              <li>• {t('subscription.troubleshooting.steps.reauthenticate', 'Log out and log back in')}</li>
              <li>• {t('subscription.troubleshooting.steps.contactSupport', 'Contact support if issue persists')}</li>
            </ul>
          </div>

          <button
            onClick={handleSuccess}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            {t('common.tryAgain', 'Try Again')}
          </button>
          <p className="text-xs text-gray-500 mt-2">
            {t('subscription.errors.contactSupport', 'If the problem persists, please contact support.')}
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSuccess;